// @ts-strict-ignore

import { useEffect, useState } from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import _ from 'lodash';
import { formatTime, TIME_FORMAT_NUMERICAL_SHORT } from '@/datetime/dateTime.utilities';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import {
  formatJournalReportContent,
  getItemIcon,
  getWorkbook,
  translateBreadcrumb,
} from '@/utilities/homescreen.utilities';
import { sqHomeScreenStore, sqWorksheetStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { ITEM_TYPES, JOURNAL_OR_REPORT, UNNAMED_REPORT } from '@/homescreen/homescreen.constants';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { subscribe } from '@/utilities/socket.utilities';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { getLockingChannel } from '@/utilities/lock.constants';
import type { IconProps } from '@seeqdev/qomponents';
import { Icon } from '@seeqdev/qomponents';

type Breadcrumb = {
  id: string;
  name: string;
  translationKey?: string;
};

interface NameCellProps {
  item: any;
  showBreadcrumbs: boolean;
  extraClassNames?: string;
  isCurrentWorkbook?: boolean;
}

export const NameCell: React.FunctionComponent<NameCellProps> = (props) => {
  const { item, showBreadcrumbs, extraClassNames, isCurrentWorkbook = false } = props;
  const { t } = useTranslation();

  const [journalReportName, setJournalReportName] = useState<string>();
  const [journalReportContent, setJournalReportContent] = useState<string>();
  const isJournalReport = _.includes(JOURNAL_OR_REPORT, item.type);
  const searchText = useFluxPath(sqHomeScreenStore, (store) => store.searchParams.textSearch) as string | undefined;
  const isAllowedLanguageCharacter = (word: string): boolean => /^[^-_/.,')'\\p{Alnum} ]+/.test(word);
  const isFoundInSearchText = (word: string): boolean => {
    if (!searchText) {
      return false;
    }

    const needle = _.toLower(searchText);
    const haystack = _.toLower(word);
    return (_.includes(needle, haystack) && isAllowedLanguageCharacter(word)) || _.includes(haystack, needle);
  };

  useEffect(() => {
    if (isJournalReport) {
      getWorkbook(item.workbookId).then((workbook) => {
        const itemWorksheet = _.find(workbook?.worksheets, { worksheetId: item.worksheetId });
        if (itemWorksheet) {
          setJournalReportName(`${workbook.name} - ${itemWorksheet.name}`);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (item.name !== t('UNNAMED') && item.name !== UNNAMED_REPORT && isJournalReport) {
      formatJournalReportContent(searchText, item.id).then((content) => setJournalReportContent(content));
    }
  }, [searchText, item]);

  const renderBreadcrumbs = (crumbs: Breadcrumb[] = []) => (
    <span data-testid="itemBreadcrumb" className="xsmall">
      {_.join(
        _.map(crumbs, (crumb) => translateBreadcrumb(crumb)),
        ' » ',
      )}
    </span>
  );

  const renderJournalReportSearchResults = (content: string) => (
    <span className="small">
      {_.map(_.split(content, /[, ]+/), (word, index) => {
        return (
          <span key={`${word}-${index}`} className={isFoundInSearchText(word) ? 'text-bold' : ''}>
            <ContainerWithHTML content={`${word} `} />
          </span>
        );
      })}
    </span>
  );

  const [localLocked, setLocalLocked] = useState(false);
  useEffect(() => {
    let unsubscribe: () => void | undefined;
    if (item.type === SeeqNames.Types.Analysis && !item.locked) {
      unsubscribe = subscribe({
        channelId: getLockingChannel(item.id),
        onMessage: () => {
          setLocalLocked(true);
        },
      });
    }
    return () => {
      unsubscribe?.();
    };
  }, []);

  const renderNameWithLockedStatus = (item: any) => (
    <div className="flexColumnContainer">
      {item.name}
      {item.locked || localLocked ? <Icon icon="fa-lock" extraClassNames="ml5" testId="lockedAnalysis" /> : <></>}
    </div>
  );

  return (
    <div className={clsx('flexRowContainer', extraClassNames)}>
      {showBreadcrumbs && renderBreadcrumbs(Array.isArray(item.ancestors) ? item.ancestors : [])}
      <span data-testid="itemName">{journalReportName ?? renderNameWithLockedStatus(item)}</span>
      {isJournalReport && !_.isNil(journalReportContent) && (
        <span data-testid="journalReportPreview">{renderJournalReportSearchResults(journalReportContent)}</span>
      )}
      {isCurrentWorkbook && <span className="xsmall">({t('EXPLORER_MODAL.CURRENT_WORKBOOK')})</span>}
    </div>
  );
};

interface HomeScreenIconCellProps {
  item: any;
  spinning?: boolean;
}

export const HomeScreenItemIconCell: React.FunctionComponent<HomeScreenIconCellProps> = (props) => {
  const { item, spinning = false } = props;
  const itemIcon = getItemIcon(props.item);

  return (
    <div data-testid={`icon_${item.id}`}>
      <IconWithSpinner
        spinning={spinning}
        icon={itemIcon}
        extraClassNames={clsx('workbenchDisplay workbookIcon', {
          workbenchAnalysis: props.item.type === ITEM_TYPES.JOURNAL,
          workbenchReport: props.item.type === ITEM_TYPES.REPORT,
          actionBoard: props.item.type === ITEM_TYPES.ACTION_BOARD,
        })}
      />
    </div>
  );
};

interface DateCellProps {
  item: any;
  accessor: string;
}

export const DateCell: React.FunctionComponent<DateCellProps> = (props) => {
  const { item, accessor } = props;

  if (!item[accessor]) {
    return null;
  }

  return <span>{formatTime(item[accessor], sqWorksheetStore.timezone, TIME_FORMAT_NUMERICAL_SHORT)}</span>;
};

export const ProgressCell: React.FunctionComponent<{ percentage: number }> = ({ percentage }) => (
  <div className="flexColumnContainer flexCenter">
    <span className="dataStatusIcon verticalAlignMiddle">
      <CircularProgressbar value={percentage} text={_.toString(percentage)} />
    </span>
  </div>
);

export const IconCell: React.FunctionComponent<IconProps> = (props) => (
  <div className="flexColumnContainer flexCenter">
    <Icon {...props} />
  </div>
);
